import {
  Modal,
  Box,
  Stack,
  TextInput,
  Select,
  Menu,
} from '@mantine/core';
import {
  IconCloud,
  IconCalendar,
  IconUsers,
  IconTrash,
  IconDoorExit,
  IconX,
  IconPencil,
  IconCheck,
  IconHash,
  IconPlus,
} from '@tabler/icons-react';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { IProject, Project } from '@/types/Project.model';
import { updateProject, addProjectMember, removeProjectMember, updateProjectMember } from '@/Api';
import { notifications } from '@mantine/notifications';
import { getCurrentUserRole, getRolePillStyles } from './index';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { EditorChipButton } from '@/components/EditorChipButton';
import classes from './ProjectModal.module.css';

interface ProjectModalProps {
  project: IProject | null;
  opened: boolean;
  onClose: () => void;
  onDelete: (projectId: string) => void;
  /** Called after the current (non-admin) user left the project. */
  onLeave: (projectId: string) => void;
  onUpdate: (updatedProject: IProject) => void;
  corporateColor: string;
  currentUserId: string;
}

export function ProjectModal({
  project,
  opened,
  onClose,
  onDelete,
  onLeave,
  onUpdate,
  corporateColor,
  currentUserId,
}: ProjectModalProps) {
  const { t } = useTranslation();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<
    typeof Project.UserRole.VIEWER | typeof Project.UserRole.EDITOR
  >(Project.UserRole.VIEWER);

  useEffect(() => {
    setIsEditingName(false);
    setEditedName('');
  }, [opened, project?._id]);

  if (!project) return null;

  const accentStyle = {
    '--project-accent': corporateColor,
    '--project-accent-soft': colorWithAlpha(corporateColor, 0.08),
    '--project-accent-border': colorWithAlpha(corporateColor, 0.22),
  } as CSSProperties;

  const iconBoxStyle: CSSProperties = {
    backgroundColor: colorWithAlpha(corporateColor, 0.1),
    borderColor: colorWithAlpha(corporateColor, 0.22),
    color: corporateColor,
  };

  const handleDelete = () => {
    if (window.confirm(t('dashboard.project.deleteProjectConfirm'))) {
      onDelete(project._id);
      onClose();
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(t('dashboard.project.leaveProjectConfirm'))) return;
    try {
      await removeProjectMember(project._id, currentUserId);
      onLeave(project._id);
      onClose();
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.project.projectLeft'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error leaving project:', error);
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.project.leaveProjectError'),
        color: 'red',
      });
    }
  };

  const handleEditName = () => {
    setEditedName(project.name);
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (editedName.trim() === '') {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.project.projectNameRequired'),
        color: 'red',
      });
      return;
    }

    try {
      const updatedProject = await updateProject(project._id, editedName.trim());
      onUpdate(updatedProject);
      setIsEditingName(false);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.project.projectNameUpdated'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating project name:', error);
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.project.projectNameUpdateError'),
        color: 'red',
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const currentUserRole = getCurrentUserRole(project, currentUserId);
  const isCurrentUserAdmin = currentUserRole === Project.UserRole.ADMIN;
  const isCurrentUserEditor = currentUserRole === Project.UserRole.EDITOR;
  const canEditProject = isCurrentUserAdmin || isCurrentUserEditor;

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.project.emailRequired'),
        color: 'red',
      });
      return;
    }

    try {
      const updatedProject = await addProjectMember(
        project._id,
        newMemberEmail.trim(),
        newMemberRole
      );
      onUpdate(updatedProject);
      setNewMemberEmail('');
      setNewMemberRole(Project.UserRole.VIEWER);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.project.memberAdded'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error adding member:', error);
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.project.memberAddError'),
        color: 'red',
      });
    }
  };

  const handleRemoveMember = async (email: string) => {
    if (window.confirm(t('dashboard.project.removeMemberConfirm'))) {
      try {
        const updatedProject = await removeProjectMember(project._id, email);
        onUpdate(updatedProject);
        notifications.show({
          title: t('dashboard.notifications.success'),
          message: t('dashboard.project.memberRemoved'),
          color: 'green',
        });
      } catch (error) {
        console.error('Error removing member:', error);
        notifications.show({
          title: t('dashboard.notifications.error'),
          message: t('dashboard.project.memberRemoveError'),
          color: 'red',
        });
      }
    }
  };

  const handleUpdateRole = async (email: string, newRole: string) => {
    try {
      const updatedProject = await updateProjectMember(
        project._id,
        email,
        newRole as typeof Project.UserRole.VIEWER | typeof Project.UserRole.EDITOR
      );
      onUpdate(updatedProject);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.project.roleUpdated'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error updating role:', error);
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.project.roleUpdateError'),
        color: 'red',
      });
    }
  };

  const renderRolePill = (role: string, clickable = false) => (
    <span
      className={`${classes.rolePill} ${clickable ? classes.rolePillClickable : ''}`}
      style={getRolePillStyles(role, corporateColor)}
    >
      {role}
    </span>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <div className={classes.modalTitleRow}>
          <div className={classes.iconBox} style={iconBoxStyle}>
            <IconCloud size={18} stroke={1.75} />
          </div>
          <div className={classes.titleBlock}>
            {isEditingName ? (
              <div className={classes.nameEditRow}>
                <TextInput
                  className={classes.nameInput}
                  value={editedName}
                  onChange={(e) => setEditedName(e.currentTarget.value)}
                  size="sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                  autoFocus
                />
                <EditorChipButton
                  variant="primary"
                  accentColor={corporateColor}
                  leftIcon={<IconCheck size={15} stroke={1.75} />}
                  onClick={handleSaveName}
                  aria-label={t('editProduct.save')}
                />
                <EditorChipButton
                  accentColor={corporateColor}
                  leftIcon={<IconX size={15} stroke={1.75} />}
                  onClick={handleCancelEdit}
                  aria-label={t('editProduct.cancel')}
                />
              </div>
            ) : (
              <>
                <h2 className={classes.projectName}>{project.name}</h2>
                <div className={classes.titleActions}>
                  {canEditProject && (
                    <EditorChipButton
                      accentColor={corporateColor}
                      leftIcon={<IconPencil size={15} stroke={1.75} />}
                      onClick={handleEditName}
                    >
                      {t('dashboard.actions.edit')}
                    </EditorChipButton>
                  )}
                  {isCurrentUserAdmin ? (
                    <EditorChipButton
                      variant="danger"
                      leftIcon={<IconTrash size={15} stroke={1.75} />}
                      onClick={handleDelete}
                    >
                      {t('dashboard.actions.delete')}
                    </EditorChipButton>
                  ) : (
                    <EditorChipButton
                      accentColor={corporateColor}
                      leftIcon={<IconDoorExit size={15} stroke={1.75} />}
                      onClick={handleLeave}
                    >
                      {t('dashboard.actions.leave')}
                    </EditorChipButton>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      }
      size="lg"
      centered
      radius="md"
      classNames={{
        content: classes.modalContent,
        header: classes.modalHeader,
        body: classes.modalBody,
      }}
      styles={{
        content: accentStyle,
      }}
    >
      <Stack gap="md">
        <div className={classes.infoCard}>
          <div className={classes.infoRow}>
            <span className={classes.infoLabel}>
              <IconHash size={13} className={classes.infoLabelIcon} stroke={1.75} />
              ID
            </span>
            <span className={classes.infoValue}>{project._id}</span>
          </div>

          <div className={classes.infoRow}>
            <span className={classes.infoLabel}>
              <IconCalendar size={13} className={classes.infoLabelIcon} stroke={1.75} />
              {t('dashboard.project.projectCreated')}
            </span>
            <span className={classes.infoValue}>
              {new Date(project.createdAt).toLocaleDateString()}
            </span>
          </div>

          <div className={classes.infoRow}>
            <span className={classes.infoLabel}>
              <IconCalendar size={13} className={classes.infoLabelIcon} stroke={1.75} />
              {t('dashboard.project.projectUpdated')}
            </span>
            <span className={classes.infoValue}>
              {new Date(project.updatedAt).toLocaleDateString()}
            </span>
          </div>

          <div className={classes.infoRow}>
            <span className={classes.infoLabel}>
              <IconUsers size={13} className={classes.infoLabelIcon} stroke={1.75} />
              {t('dashboard.project.projectMembers')}
            </span>
            <span className={classes.infoValue}>{project.projectUsers.length}</span>
          </div>
        </div>

        <Box>
          <div className={classes.sectionHeader}>
            <h3 className={classes.sectionTitle}>{t('dashboard.project.projectMembers')}</h3>
            <span className={classes.memberCountChip}>{project.projectUsers.length}</span>
          </div>

          {isCurrentUserAdmin && (
            <div className={classes.addMemberCard}>
              <div className={classes.addMemberRow}>
                <TextInput
                  className={classes.addMemberEmail}
                  placeholder={t('dashboard.project.memberEmail')}
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.currentTarget.value)}
                  size="sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void handleAddMember();
                    }
                  }}
                />
                <Select
                  data={[
                    { value: Project.UserRole.VIEWER, label: Project.UserRole.VIEWER },
                    { value: Project.UserRole.EDITOR, label: Project.UserRole.EDITOR },
                  ]}
                  value={newMemberRole}
                  onChange={(value) =>
                    setNewMemberRole(
                      (value as typeof Project.UserRole.VIEWER | typeof Project.UserRole.EDITOR) ||
                        Project.UserRole.VIEWER
                    )
                  }
                  size="sm"
                  w={108}
                />
                <EditorChipButton
                  variant="primary"
                  accentColor={corporateColor}
                  leftIcon={<IconPlus size={15} stroke={1.75} />}
                  onClick={() => void handleAddMember()}
                >
                  {t('editProduct.add')}
                </EditorChipButton>
              </div>
            </div>
          )}

          <div className={classes.memberList}>
            {project.projectUsers.map((projectUser, index) => (
              <div key={index} className={classes.memberCard}>
                <div className={classes.memberMain}>
                  <div className={classes.memberAvatar} aria-hidden>
                    {projectUser.email.charAt(0).toUpperCase()}
                  </div>
                  <span className={classes.memberEmail} title={projectUser.email}>
                    {projectUser.email}
                  </span>
                </div>

                <div className={classes.memberActions}>
                  {isCurrentUserAdmin &&
                  projectUser.userId !== currentUserId &&
                  projectUser.role !== Project.UserRole.ADMIN ? (
                    <Menu shadow="md" width={120} floatingStrategy="fixed">
                      <Menu.Target>
                        <button
                          type="button"
                          className={`${classes.rolePill} ${classes.rolePillClickable} ${classes.rolePillButton}`}
                          style={getRolePillStyles(projectUser.role, corporateColor)}
                        >
                          {projectUser.role}
                        </button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          onClick={() =>
                            handleUpdateRole(projectUser.email, Project.UserRole.VIEWER)
                          }
                        >
                          Viewer
                        </Menu.Item>
                        <Menu.Item
                          onClick={() =>
                            handleUpdateRole(projectUser.email, Project.UserRole.EDITOR)
                          }
                        >
                          Editor
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  ) : (
                    renderRolePill(projectUser.role)
                  )}

                  {isCurrentUserAdmin && projectUser.userId !== currentUserId && (
                    <EditorChipButton
                      variant="danger"
                      leftIcon={<IconX size={14} stroke={1.75} />}
                      onClick={() => void handleRemoveMember(projectUser.email)}
                      aria-label={t('editProduct.remove')}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Box>
      </Stack>
    </Modal>
  );
}
